/**
 * 我的合集列表组件
 *
 * MyPage 的合集 tab 内容。结构与 MyVideoList 类似，但简化：
 * - 一次性拉取 mine=1 的全部合集（合集数一般 ≤ 100，无需做无限滚动）
 * - 顶部有"新建合集"按钮
 * - 每个卡片：封面 / 标题 / 集数 / 可见性 / 编辑、删除
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { clearAuth, getAuthHeaders, type User } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import type { Collection, CollectionListResponseData } from '../types/collection';
import { CollectionCard } from './CollectionCard';
import { ConfirmDialog } from './ConfirmDialog';
import './MyCollectionList.css';

interface MyCollectionListProps {
  user: User | null;
  onEmptyStateChange?: (isEmpty: boolean) => void;
  onListLoadingChange?: (isLoading: boolean) => void;
}

export const MyCollectionList: React.FC<MyCollectionListProps> = ({
  user,
  onEmptyStateChange,
  onListLoadingChange,
}) => {
  const navigate = useNavigate();

  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });

  useEffect(() => {
    onEmptyStateChange?.(collections.length === 0 && !loading);
  }, [collections.length, loading, onEmptyStateChange]);

  useEffect(() => {
    onListLoadingChange?.(loading && collections.length === 0);
  }, [loading, collections.length, onListLoadingChange]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/collections?mine=1&page=1&pageSize=100`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as ApiResponse<CollectionListResponseData>;
      if (data.success && data.data) {
        setCollections(data.data.list || []);
      }
    } catch (err) {
      console.error('[MyCollectionList] 加载合集失败:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCreate = () => {
    navigate('/collections/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/collections/${id}/edit`);
  };

  const handleDelete = (id: string) => {
    setDeleteDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    const id = deleteDialog.id;
    setDeleteDialog({ open: false, id: null });
    if (!id) return;

    try {
      const res = await fetch(`${API_BASE_URL}/collections/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data = (await res.json()) as ApiResponse;
      if (data.success) {
        setCollections((prev) => prev.filter((c) => c.id !== id));
      } else {
        window.alert(data.error || '删除合集失败');
      }
    } catch (err) {
      console.error('[MyCollectionList] 删除合集失败:', err);
      window.alert('网络错误，请稍后重试');
    }
  };

  const toDelete = deleteDialog.id
    ? collections.find((c) => c.id === deleteDialog.id) || null
    : null;

  return (
    <>
      <ConfirmDialog
        isOpen={deleteDialog.open}
        title="确认删除合集"
        message={`确定要删除合集 "${toDelete?.name || '未知'}" 吗？此操作不会删除合集中的视频本身。`}
        confirmText="删除"
        cancelText="取消"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialog({ open: false, id: null })}
      />

      <div className="my-collections-toolbar">
        <button type="button" className="my-collections-create-btn" onClick={handleCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>新建合集</span>
        </button>
      </div>

      {collections.map((c) => (
        <CollectionCard
          key={c.id}
          collection={c}
          currentUserId={user?.id || null}
          onEdit={handleEdit}
          onDelete={handleDelete}
          returnTo="/profile?tab=collections"
          allowCollectionDetailManage
        />
      ))}
    </>
  );
};
